import { useEffect, useState } from 'react';
import IconArrow from '../../components/icons/IconArrow';
import IconAdd from '../../components/icons/IconAdd';
import IconDelete from '../../components/icons/IconDelete';
import { LEVELS_BY_KEY } from './courseLevels';
import { sortByPosition } from './ordering';

// A single dog-eared page marks a Topic row as actual content rather than
// structure - an open-book version of this was tried first but read as
// "a book" more than "a page," which is the closer fit (a Topic is one
// page/unit of content, not a whole volume). The folded corner is now a
// small solid accent triangle (rather than just another outline) and the
// text lines are trimmed to 2 for a cleaner, less busy look at this size.
// Same hand-built-SVG convention throughout this module (viewBox 24x24,
// stroke-only except the one filled accent, only line/polygon primitives
// so it renders crisp this small). Exported so PreviewNavList.jsx's flat
// topic rows and PreviewBreadcrumb.jsx's Topic chip both reuse the exact
// same glyph instead of separate copies.
export function TopicIcon() {
   return (
      <svg viewBox='0 0 24 24' width='13' height='13' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <polygon points='6,2 15,2 19,6 19,22 6,22' />
         <polygon points='15,2 19,6 15,6' fill='currentColor' stroke='none' />
         <line x1='9' y1='11' x2='16' y2='11' />
         <line x1='9' y1='15' x2='16' y2='15' />
      </svg>
   );
}

// Generic "click to add" row - works for both structural creation
// (onAdd -> actions.createNode) and topic creation (onAdd -> actions.createTopic),
// the caller supplies the label + the create call. `primary` styles the
// "add a Topic" row as the accent action (creating actual content is the
// point) versus the plain "add the next structural level" row.
function AddRow({ label, onAdd, primary }) {
   const [open, setOpen] = useState(false);
   const [name, setName] = useState('');
   const [saving, setSaving] = useState(false);

   const handleAdd = () => {
      if(!name.trim() || saving) return;
      setSaving(true);
      onAdd(name.trim())
         .then(() => { setName(''); setOpen(false); })
         .catch(() => window.alert('Не вдалося створити елемент.'))
         .finally(() => setSaving(false));
   };

   if(!open) {
      return (
         <div className={'course-tree-add-row' + (primary ? ' primary' : '')} onClick={() => setOpen(true)}>
            <IconAdd />
            <span>Додати: {label}</span>
         </div>
      );
   }
   return (
      <div className='course-tree-add-form'>
         <input className='submit-field from-input txt-c course-tree-add-input' autoFocus placeholder={label}
            value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => {
               if(e.key === 'Enter') handleAdd();
               if(e.key === 'Escape') { setOpen(false); setName(''); }
            }} />
         <div className='course-tree-add-confirm' onClick={handleAdd} title='Додати'>✓</div>
         <div className='course-tree-add-cancel' onClick={() => { setOpen(false); setName(''); }} title='Скасувати'>×</div>
      </div>
   );
}

// A Topic leaf, hosted directly under whichever level it's attached to
// (course/module/chapter/unit/lesson - hostLevelKey/hostId identify that).
// Its own row/action shape differs enough from a structural OutlineNode
// (never expandable, uses the topic-specific actions.*Topic* functions)
// that it's a separate small component rather than an OutlineNode branch.
// Constructor-only - the Preview tab renders its own flat topic rows in
// PreviewNavList.jsx instead (this one always shows move/delete actions).
function TopicRow({ topic, hostLevelKey, hostId, actions, canMoveUp, canMoveDown }) {
   const nodeKey = 'topic:' + topic.id;
   const selected = actions.isSelected('topic', topic.id);
   const deleting = actions.deletingKey === nodeKey;

   const handleSelect = () => actions.select('topic', topic.id, hostId, hostLevelKey);
   const handleMove = (e, direction) => {
      e.stopPropagation();
      actions.reorderTopicSibling(hostLevelKey, hostId, topic.id, direction);
   };
   const handleDelete = (e) => {
      e.stopPropagation();
      if(deleting) return;
      actions.deleteTopic(hostLevelKey, hostId, topic);
   };

   return (
      <div className={'course-tree-row course-tree-row-topic' + (selected ? ' selected' : '')}
         onClick={handleSelect}>
         <span className='course-tree-topic-icon' title='Тема'><TopicIcon /></span>
         <span className='course-tree-node-label'>{topic.name}</span>
         <div className='course-tree-row-actions' onClick={e => e.stopPropagation()}>
            <div className={'course-tree-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={(e) => canMoveUp && handleMove(e, -1)} title='Вгору'>
               <IconArrow style={{ transform: 'rotate(-90deg)', height: 10, width: 10 }} />
            </div>
            <div className={'course-tree-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={(e) => canMoveDown && handleMove(e, 1)} title='Вниз'>
               <IconArrow style={{ transform: 'rotate(90deg)', height: 10, width: 10 }} />
            </div>
            <div className={'course-tree-delete-btn' + (deleting ? ' disabled' : '')} onClick={handleDelete} title='Видалити'>
               <IconDelete style={{ fill: '#e0876c', width: 12, height: 12 }} />
            </div>
         </div>
      </div>
   );
}

// Renders everything that belongs to ONE parent node: its own directly-
// attached Topics first (own content), then its structural sub-items below
// (further nesting) - "your content, then further structure". `levelKey`
// here is the PARENT's own level (e.g. 'module'), not the child type being
// listed - that's what lets a single call site look up both
// canHostTopics (topics live directly on this level) and childKey (the
// structural next level) together. Fetch is mount/visibility-driven: these
// effects fire exactly when a ChildrenList becomes rendered - i.e. when the
// course loads (root, levelKey='course') or when a node is expanded
// (nested) - not on every render.
//
// Indentation is no longer a per-row inline pixel offset computed from an
// explicit `depth` counter - `.course-tree-children` itself carries a fixed
// margin/border/padding step in CSS, so nesting these divs (one per
// expanded level) both indents AND draws a continuous guide line down the
// left edge of every group of siblings, purely from real DOM nesting. The
// same wrapper's translucent background compounds with depth for free too
// (each nested layer paints on top of its ancestors' - see courses.css).
//
// Constructor-only (see OutlineTree.jsx) - the Preview tab has its own
// flat, non-nested walk in PreviewNavList.jsx instead of rendering this.
export function ChildrenList({ levelKey, parentId, actions }) {
   const level = LEVELS_BY_KEY[levelKey];
   const childLevel = level.childKey ? LEVELS_BY_KEY[level.childKey] : null;

   useEffect(() => {
      if(level.canHostTopics) actions.ensureTopics(levelKey, parentId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [levelKey, parentId]);

   useEffect(() => {
      if(childLevel) actions.ensureChildren(childLevel.key, parentId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [levelKey, parentId]);

   const topics = level.canHostTopics ? actions.getTopics(levelKey, parentId) : [];
   const children = childLevel ? actions.getChildren(childLevel.key, parentId) : [];
   const stillLoading = (level.canHostTopics && topics === undefined) || (childLevel && children === undefined);

   if(stillLoading) {
      return <div className='course-tree-children'><div className='course-tree-loading'>...</div></div>;
   }

   const sortedTopics = sortByPosition(topics || []);
   const sortedChildren = sortByPosition(children || []);

   return (
      <div className='course-tree-children'>
         {level.canHostTopics ? (
            <div className='course-tree-topics-group'>
               {sortedTopics.map((topic, index) => (
                  <TopicRow key={'topic-' + topic.id} topic={topic} hostLevelKey={levelKey} hostId={parentId}
                     actions={actions}
                     canMoveUp={index > 0} canMoveDown={index < sortedTopics.length - 1} />
               ))}
               <AddRow label={LEVELS_BY_KEY.topic.label} primary
                  onAdd={(name) => actions.createTopic(levelKey, parentId, { name })} />
            </div>
         ) : ''}
         {childLevel ? (
            <div className='course-tree-structure-group'>
               {sortedChildren.map((node, index) => (
                  <OutlineNode key={node.id} levelKey={childLevel.key} node={node} parentId={parentId}
                     actions={actions}
                     canMoveUp={index > 0} canMoveDown={index < sortedChildren.length - 1} />
               ))}
               <AddRow label={childLevel.label}
                  onAdd={(name) => actions.createNode(childLevel.key, parentId, { name })} />
            </div>
         ) : ''}
      </div>
   );
}

export default function OutlineNode({ levelKey, node, parentId, actions, canMoveUp, canMoveDown }) {
   const level = LEVELS_BY_KEY[levelKey];
   const expandable = !!(level.childKey || level.canHostTopics);
   const nodeKey = levelKey + ':' + node.id;
   const expanded = actions.isExpanded(levelKey, node.id);
   const selected = actions.isSelected(levelKey, node.id);
   const deleting = actions.deletingKey === nodeKey;

   const handleToggle = (e) => {
      e.stopPropagation();
      actions.toggleExpand(levelKey, node.id);
   };
   const handleSelect = () => actions.select(levelKey, node.id, parentId);
   const handleMove = (e, direction) => {
      e.stopPropagation();
      actions.reorderSibling(levelKey, parentId, node.id, direction);
   };
   const handleDelete = (e) => {
      e.stopPropagation();
      if(deleting) return;
      actions.deleteNode(levelKey, parentId, node);
   };

   return (
      <div className='course-tree-node'>
         <div className={'course-tree-row' + (selected ? ' selected' : '')}
            onClick={handleSelect}>
            {expandable ? (
               <div className={'course-tree-chevron' + (expanded ? ' expanded' : '')} onClick={handleToggle}>
                  <IconArrow style={{ height: 10, width: 10 }} />
               </div>
            ) : <span className='course-tree-chevron-spacer' />}
            <span className='course-tree-node-label'>{node.name}</span>
            <div className='course-tree-row-actions' onClick={e => e.stopPropagation()}>
               <div className={'course-tree-move-btn' + (!canMoveUp ? ' disabled' : '')} onClick={(e) => canMoveUp && handleMove(e, -1)} title='Вгору'>
                  <IconArrow style={{ transform: 'rotate(-90deg)', height: 10, width: 10 }} />
               </div>
               <div className={'course-tree-move-btn' + (!canMoveDown ? ' disabled' : '')} onClick={(e) => canMoveDown && handleMove(e, 1)} title='Вниз'>
                  <IconArrow style={{ transform: 'rotate(90deg)', height: 10, width: 10 }} />
               </div>
               <div className={'course-tree-delete-btn' + (deleting ? ' disabled' : '')} onClick={handleDelete} title='Видалити'>
                  <IconDelete style={{ fill: '#e0876c', width: 12, height: 12 }} />
               </div>
            </div>
         </div>
         {expanded && expandable ? (
            <ChildrenList levelKey={levelKey} parentId={node.id} actions={actions} />
         ) : ''}
      </div>
   );
}
